import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useBlocker } from 'react-router';
import { formatAmountInput, formatEuro, formatInteger, parseAmountInput } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { portalKeys, submitProfileChanges } from '../api';
import frame from '../frame/Portal.module.css';
import phone from '../frame/Phone.module.css';
import { countWord } from '../overview/actions';
import type { PortalAddressValue, PortalProfile, PortalProfileChange } from '../types';
import styles from './Profile.module.css';

type AmountKey = 'net_income' | 'special_payments' | 'equity';

const AMOUNTS: { key: AmountKey; label: string; source: keyof PortalProfile['edit'] }[] = [
  { key: 'net_income', label: 'Nettoeinkommen', source: 'netIncomeCents' },
  { key: 'special_payments', label: 'Sonderzahlungen jährlich', source: 'specialPaymentsCents' },
  { key: 'equity', label: 'Eigenkapital', source: 'equityCents' },
];

/** Editing text of an amount: whole euros without decimals („5.850"), else „1.234,50". */
function amountText(cents: number | null): string {
  if (cents === null) return '';
  return cents % 100 === 0 ? formatInteger(cents / 100) : formatAmountInput(cents);
}

interface Draft {
  phone: string;
  /** „Ostengasse 14, 93047 Regensburg" — one line as in the frame, split for the API on submit. */
  address: string;
  amounts: Record<AmountKey, string>;
}

function initialDraft(edit: PortalProfile['edit']): Draft {
  return {
    phone: edit.phone ?? '',
    address: edit.addressLabel ?? '',
    amounts: {
      net_income: amountText(edit.netIncomeCents),
      special_payments: amountText(edit.specialPaymentsCents),
      equity: amountText(edit.equityCents),
    },
  };
}

function originalCents(edit: PortalProfile['edit'], key: AmountKey): number | null {
  const source = AMOUNTS.find((amount) => amount.key === key)?.source;
  const value = source ? edit[source] : null;
  return typeof value === 'number' ? value : null;
}

const ADDRESS_FORMAT_MESSAGE = 'Bitte im Format „Straße Hausnummer, PLZ Ort" angeben.';
const AMOUNT_MESSAGE = 'Bitte tragen Sie einen Betrag ein.';

/** „Ostengasse 14, 93047 Regensburg" → street, postal code, city (null when the line does not read like that). */
function parseAddress(line: string): PortalAddressValue | null {
  const match = /^(.+?),\s*(\d{4,5})\s+(.+)$/.exec(line.trim());
  if (!match) return null;
  const [, street = '', postalCode = '', city = ''] = match;
  return { street: street.trim(), postalCode, city: city.trim() };
}

type EditKey = AmountKey | 'address';

/** The changes of the draft against the profile (unchanged values are left out) and the unreadable fields. */
function changesOf(
  edit: PortalProfile['edit'],
  draft: Draft,
): { changes: PortalProfileChange[]; invalid: EditKey[] } {
  const changes: PortalProfileChange[] = [];
  const invalid: EditKey[] = [];
  if (draft.phone.trim() !== (edit.phone ?? '').trim())
    changes.push({ field: 'phone', value: draft.phone.trim() });
  if (draft.address.trim() !== (edit.addressLabel ?? '').trim()) {
    const address = parseAddress(draft.address);
    if (address === null) invalid.push('address');
    else changes.push({ field: 'address', value: address });
  }
  for (const amount of AMOUNTS) {
    const text = draft.amounts[amount.key];
    const original = originalCents(edit, amount.key);
    if (text.trim() === amountText(original).trim()) continue;
    const cents = parseAmountInput(text);
    if (cents === null || cents < 0) invalid.push(amount.key);
    else if (cents !== original) changes.push({ field: amount.key, value: cents });
  }
  return { changes, invalid };
}

/**
 * Mandantenportal 21 „Profil · Bearbeitungsmodus": the warning banner when a financial field changed, the name
 * read-only, phone, the address in one line (split into street, postal code and city for the API), the amounts with
 * „geändert" + „Bisher …", the bar „Eine nicht gespeicherte Änderung" with „Verwerfen" / „Änderungen einreichen".
 * Leaving the page with unsaved changes asks first (in-app navigation and `beforeunload`).
 */
export function ProfileEdit({ profile, onDone }: { profile: PortalProfile; onDone: () => void }) {
  const queryClient = useQueryClient();
  const edit = profile.edit;
  const [draft, setDraft] = useState<Draft>(() => initialDraft(edit));
  const [pending, setPending] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const { changes, invalid } = changesOf(edit, draft);
  const changedFields = new Set<string>([...changes.map((change) => change.field), ...invalid]);
  const dirty = changedFields.size > 0;
  const financialChanged = AMOUNTS.some((amount) => changedFields.has(amount.key));
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  function setAmount(key: AmountKey, value: string) {
    setDraft((current) => ({ ...current, amounts: { ...current.amounts, [key]: value } }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBanner(null);
    if (invalid.length > 0) {
      setFieldErrors(
        Object.fromEntries(
          invalid.map((key) => [key, key === 'address' ? ADDRESS_FORMAT_MESSAGE : AMOUNT_MESSAGE]),
        ),
      );
      return;
    }
    setFieldErrors({});
    setPending(true);
    try {
      await submitProfileChanges(changes);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: portalKeys.profile }),
        queryClient.invalidateQueries({ queryKey: portalKeys.overview }),
      ]);
      onDone();
    } catch (cause) {
      if (cause instanceof ApiError && Object.keys(cause.fields).length > 0) {
        const fields = cause.fields;
        setFieldErrors({ phone: fields['changes.phone'] ?? '', ...fields });
        setBanner(Object.values(fields)[0] ?? cause.message);
      } else {
        setBanner(errorMessage(cause));
      }
    } finally {
      setPending(false);
    }
  }

  const count = changedFields.size;
  const barText =
    count === 0
      ? 'Keine Änderungen'
      : `${countWord(count, true)} nicht gespeicherte ${count === 1 ? 'Änderung' : 'Änderungen'}`;

  return (
    <form
      className={clsx(frame.content, styles.editPage)}
      onSubmit={(event) => void submit(event)}
      noValidate
    >
      <h1 className={styles.srOnly}>Mein Profil bearbeiten</h1>
      {financialChanged ? (
        <div className={styles.warning} role="status">
          <TriangleAlert size={18} strokeWidth={1.75} aria-hidden="true" />
          <span>{edit.financialBanner}</span>
        </div>
      ) : null}
      {banner ? <Banner tone="danger">{banner}</Banner> : null}
      <div className={styles.grid}>
        <section className={clsx(frame.card, styles.card)} aria-labelledby="portal-edit-person">
          <h2 className={frame.eyebrow} id="portal-edit-person">
            Person
          </h2>
          <div className={styles.editFields}>
            <div>
              <span className={phone.label} id="portal-edit-name-label">
                Name
              </span>
              <div
                className={styles.readonly}
                role="textbox"
                aria-readonly="true"
                aria-labelledby="portal-edit-name-label"
                aria-describedby="portal-edit-name-hint"
              >
                {edit.name}
              </div>
              <div className={phone.hint} id="portal-edit-name-hint">
                {edit.nameHint}
              </div>
            </div>
            <EditField
              id="portal-edit-phone"
              label="Telefon"
              changed={changedFields.has('phone')}
              before={edit.phone}
              error={fieldErrors['phone'] || undefined}
            >
              <input
                id="portal-edit-phone"
                className={clsx(phone.input, styles.editInput)}
                type="tel"
                value={draft.phone}
                onChange={(event) => setDraft((current) => ({ ...current, phone: event.target.value }))}
                autoComplete="tel"
              />
            </EditField>
            <EditField
              id="portal-edit-address"
              label="Anschrift"
              changed={changedFields.has('address')}
              before={edit.addressLabel}
              error={fieldErrors['address'] || undefined}
            >
              <input
                id="portal-edit-address"
                className={clsx(phone.input, styles.editInput)}
                value={draft.address}
                onChange={(event) => setDraft((current) => ({ ...current, address: event.target.value }))}
                autoComplete="street-address"
              />
            </EditField>
          </div>
        </section>
        <section className={clsx(frame.card, styles.card)} aria-labelledby="portal-edit-finances">
          <h2 className={frame.eyebrow} id="portal-edit-finances">
            Finanzen
          </h2>
          <div className={styles.editFields}>
            {AMOUNTS.map((amount) => {
              const original = originalCents(edit, amount.key);
              return (
                <EditField
                  key={amount.key}
                  id={`portal-edit-${amount.key}`}
                  label={amount.label}
                  changed={changedFields.has(amount.key)}
                  before={original === null ? null : formatEuro(original)}
                  error={fieldErrors[amount.key]}
                  suffix="€"
                >
                  <input
                    id={`portal-edit-${amount.key}`}
                    className={clsx(phone.input, styles.editInput, styles.amountInput)}
                    inputMode="decimal"
                    value={draft.amounts[amount.key]}
                    onChange={(event) => setAmount(amount.key, event.target.value)}
                  />
                </EditField>
              );
            })}
          </div>
        </section>
      </div>
      <div className={styles.bar}>
        <span className={styles.barText}>{barText}</span>
        <button
          type="button"
          className={styles.discard}
          onClick={() => {
            setDraft(initialDraft(edit));
            setFieldErrors({});
            setBanner(null);
            onDone();
          }}
        >
          Verwerfen
        </button>
        <button
          type="submit"
          className={clsx('pressable', frame.primary, styles.submit)}
          disabled={!dirty || pending}
        >
          Änderungen einreichen
        </button>
      </div>
      <Dialog
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        title="Änderungen verwerfen?"
        width={480}
        overlayTop={0}
        footer={
          <>
            <Button variant="secondary" onClick={() => blocker.reset?.()}>
              Weiter bearbeiten
            </Button>
            <Button variant="destructiveFilled" onClick={() => blocker.proceed?.()}>
              Verwerfen
            </Button>
          </>
        }
      >
        Ihre Änderungen sind noch nicht eingereicht.
      </Dialog>
    </form>
  );
}

function EditField({
  id,
  label,
  changed,
  before,
  error,
  suffix,
  children,
}: {
  id: string;
  label: string;
  changed: boolean;
  before: string | null;
  error?: string | undefined;
  suffix?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div>
      <div className={styles.labelRow}>
        <label className={phone.label} htmlFor={id}>
          {label}
        </label>
        {changed ? <span className={styles.changedPill}>geändert</span> : null}
      </div>
      <div className={clsx(phone.control, styles.editControl, error && phone.controlInvalid)}>
        {children}
        {suffix ? <span className={styles.suffix}>{suffix}</span> : null}
      </div>
      {error ? (
        <div className={phone.fieldError} role="alert">
          <TriangleAlert size={14} strokeWidth={1.75} aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : changed && before ? (
        <div className={phone.hint}>Bisher {before}</div>
      ) : null}
    </div>
  );
}
